import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { businessInfo, whatsappNumber, siteContent } from '../data/mock';
import { MapPin, Phone, Instagram, MessageCircle, Mail, Building2 } from 'lucide-react';
import { Button } from './ui/button';

const Contact = () => {
  const { t, language } = useLanguage();

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(siteContent[language].whatsappMessages.general);
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
  };

  return (
    <section id="contact" className="py-24 bg-stone-900 text-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-rose-400/50 to-transparent" />
      <div className="absolute top-20 right-0 w-96 h-96 bg-rose-900/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-rose-400 text-sm tracking-widest uppercase font-medium">
            Contact
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-white mt-3 mb-4">
            {t.contact.title}
          </h2>
          <div className="w-16 h-1 bg-rose-400 mx-auto mb-6" />
          <p className="text-stone-400 text-lg">{t.contact.subtitle}</p>
        </div>

        {/* Contact Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {/* Location */}
          <div className="text-center p-6 rounded-2xl bg-stone-800/50 backdrop-blur-sm hover:bg-stone-800 transition-colors duration-300 group">
            <div className="w-14 h-14 bg-rose-400/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-rose-400/30 transition-colors">
              <MapPin className="w-6 h-6 text-rose-400" />
            </div>
            <h3 className="font-medium text-white mb-2">{t.contact.address}</h3>
            <p className="text-stone-400 text-sm">{t.contact.city}</p>
            <p className="text-stone-500 text-xs mt-1">{t.contact.building}</p>
          </div>

          {/* Phone / WhatsApp */}
          <button
            onClick={handleWhatsAppClick}
            className="text-center p-6 rounded-2xl bg-stone-800/50 backdrop-blur-sm hover:bg-green-900/30 transition-colors duration-300 group"
          >
            <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-green-500/30 transition-colors">
              <MessageCircle className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="font-medium text-white mb-2">WhatsApp</h3>
            <p className="text-stone-400 text-sm">{t.contact.phone}</p>
          </button>

          {/* Instagram */}
          <a
            href={businessInfo.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="text-center p-6 rounded-2xl bg-stone-800/50 backdrop-blur-sm hover:bg-pink-900/30 transition-colors duration-300 group"
          >
            <div className="w-14 h-14 bg-pink-500/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-pink-500/30 transition-colors">
              <Instagram className="w-6 h-6 text-pink-400" />
            </div>
            <h3 className="font-medium text-white mb-2">Instagram</h3>
            <p className="text-stone-400 text-sm">{t.contact.instagram}</p>
          </a>

          {/* Location/Salon */}
          <div className="text-center p-6 rounded-2xl bg-stone-800/50 backdrop-blur-sm hover:bg-stone-800 transition-colors duration-300 group">
            <div className="w-14 h-14 bg-rose-400/20 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-rose-400/30 transition-colors">
              <Building2 className="w-6 h-6 text-rose-400" />
            </div>
            <h3 className="font-medium text-white mb-2">Supreme Beauty</h3>
            <p className="text-stone-400 text-sm">& Wellness</p>
          </div>
        </div>

        {/* Map Embed */}
        <div className="rounded-2xl overflow-hidden shadow-2xl h-80">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2836.5!2d-64.8!3d46.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4ca0b9!2s44%20Mapleton%20Rd%2C%20Moncton%2C%20NB!5e0!3m2!1sen!2sca!4v1"
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'grayscale(50%) contrast(1.1)' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="AB Nail Lounge Location"
          />
        </div>
      </div>
    </section>
  );
};

export default Contact;
